import { BUTTON_SIZE, BUTTON_TYPE, COLORS, DSButton } from '@repo/design-system/index'
import {
  QIcon_certified,
  QIcon_check_filled,
  QIcon_check_outline,
  QIcon_pause_circle,
  QIcon_volumne,
} from '@repo/icons/index'
import classNames from 'classnames'
import { useContext, useMemo, useState } from 'react'
import { useAITutorSummaryMembership, useCourseDetail } from '../../../modules/apis/student/ai-tutor'
import { n_ai_tutor, n_home } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { getAITutorTranslation } from '../../../modules/utils'
import { SoundContext, SoundProvider } from '../../common/audio/play/Audio'
import { AITutorEnterLesson, CircularProgress, LinearProgress } from './Common'

const tempImageUrl = 'https://d38emex6h5e12i.cloudfront.net/ai-tutor/course/contract_review.png'

export const AITutorRoleplayCardHalf = ({ onClick = null, roleplay = null }) => {
  const { locale } = useTranslation()
  return (
    <div
      className="rounded-[8px] border-[1.5px] border-gray-200 flex flex-col items-start justify-start flex-1 px-[16px] py-[12px] gap-[12px]"
      onClick={onClick}
    >
      <div className="rounded-tl-lg rounded-tr-lg self-stretch shrink-0 flex justify-start">
        <img src={roleplay?.image_url || tempImageUrl} className="w-[92px] h-[92px] rounded-[8px]" />
        {/* <div className="bg-[rgba(255,255,255,0.50)] rounded-2xl p-[4px] flex flex-row items-start justify-start">
          <QIcon_bookmark />
        </div> */}
      </div>
      <div className="flex flex-col items-start justify-start self-stretch shrink-0">
        <div className="flex flex-col gap-2 items-start justify-start self-stretch shrink-0">
          <div className="flex flex-row items-start justify-start self-stretch shrink-0 my-[8px]">
            <div className="flex flex-row items-start justify-start shrink-0">
              <div className="bg-gray-100 rounded px-[6px] py-[2px] flex flex-row items-start justify-start shrink-0">
                <div className="text-gray-600 text-left Overline-10Bd ">{roleplay?.job_category}</div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 items-start justify-start self-stretch shrink-0">
            <div className="text-black text-left Body1-16Bd self-stretch overflow-hidden overflow-ellipsis truncate-2-lines mb-[8px]">
              {getAITutorTranslation(roleplay?.title, locale)}
            </div>
            <div className="text-gray-600 text-left Body2-14Md self-stretch overflow-hidden overflow-ellipsis truncate-2-lines">
              {getAITutorTranslation(roleplay?.description, locale)}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export const AITutorRolePlayCardCourseAfter = ({ onClick, roleplay }) => {
  const { t, locale } = useTranslation()

  return (
    <div
      className="flex flex-col gap-[16px] items-center justify-center relative w-full self-stretch"
      onClick={onClick}
    >
      <div className="rounded-lg px-[12px] flex flex-col gap-[12px] items-center justify-center relative">
        <div className="flex flex-row gap-2 items-center justify-center relative">
          <div className="text-gray-500 text-center Body1-16Bd relative flex-1">{t(n_ai_tutor.study.msg_roleplay)}</div>
        </div>
      </div>
      <div className="bg-white rounded-xl p-[16px] gap-8 justify-start overflow-hidden relative w-full">
        <div className="flex flex-row gap-[12px] items-start justify-start relative ">
          <img src={roleplay?.image_url || tempImageUrl} className="w-[72px] h-[72px] rounded-[8px]" />
          <div className="flex flex-col gap-8 items-start justify-start relative w-full">
            <div className="bg-gray-100 rounded px-[6px] py-[2px] flex flex-row items-start justify-start">
              <div className="text-gray-600 text-left Overline-10Bd ">{roleplay?.job_category}</div>
            </div>
            <div className="text-gray-800 text-left Body1-16Bd relative overflow-hidden truncate truncate-2-lines">
              {getAITutorTranslation(roleplay?.title, locale)}
            </div>
            <div className="text-gray-500 Body2-14Md max-w-[220px] whitespace-nowrap text-ellipsis overflow-hidden truncate">
              {getAITutorTranslation(roleplay?.description, locale)}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export const AITutorLessonCard = ({ lesson, onSelect, active, order, pos, progress, tabId, inactive = false }) => {
  const { t } = useTranslation()
  const { locale } = useTranslation()

  const isCompleted = progress === 100

  const getButtonName = () => {
    if (progress === 0) {
      return t(n_ai_tutor.study.start_btn)
    } else if (isCompleted) {
      return t(n_ai_tutor.study.again_btn)
    } else {
      return t(n_ai_tutor.study.continue_btn)
    }
  }

  return (
    <div
      className={`${
        active ? 'bg-white' : 'bg-gray-50'
      } rounded-xl p-[16px] flex flex-col items-center self-stretch shrink-0 relative`}
      style={
        active
          ? {
              boxShadow: 'var(--shadow-on-gray-100-box-shadow, 0px 1px 4px 0px rgba(0, 0, 0, 0.2))',
            }
          : {}
      }
    >
      <div className="flex flex-row gap-x-[12px] items-start self-stretch shrink-0 relative" onClick={onSelect}>
        <div
          className={classNames(
            'rounded-[100px] p-[14.4px] flex flex-col gap-2 items-center justify-center shrink-0 w-[48px] h-[48px] relative',
            isCompleted ? 'bg-green-50' : 'bg-white'
          )}
        >
          {isCompleted ? (
            <QIcon_check_filled width={22} height={22} color={COLORS.GREEN500} />
          ) : (
            <div
              className={classNames(
                'text-center text-base leading-[140%] font-bold relative flex items-center justify-center',
                active ? 'text-black' : 'text-gray-500'
              )}
            >
              {order.toString().padStart(2, '0')}
            </div>
          )}
          <CircularProgress progress={lesson?.progress} size={48} width={8} rail />
        </div>
        <div className="flex flex-col gap-[16px] items-start justify-start flex-1 relative">
          <div className="flex flex-col gap-[12px] items-start justify-start shrink-0 relative">
            <div className="flex flex-col gap-0 items-start justify-start self-stretch shrink-0 relative">
              <div
                className={`text-left ${
                  active ? 'Title2-18Bd text-black' : 'Body1-16Md text-gray-600'
                } relative self-stretch overflow-hidden truncate-2-lines`}
              >
                {getAITutorTranslation(lesson?.title, locale)}
              </div>
              {active && <div ref={pos}></div>}
              {(locale === 'ko' || locale === 'ja') && (
                <div
                  className={classNames(
                    'text-left Body2-14Md relative self-stretch overflow-hidden truncate-2-lines',
                    inactive ? 'text-gray-300' : 'text-gray-500'
                  )}
                >
                  {lesson?.title?.en}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {active && (
        <div className="w-full pt-[16px]">
          <AITutorEnterLesson
            lessonId={lesson?.id}
            tabId={tabId}
            comp={(onClick, loading) => (
              <DSButton
                onClick={onClick}
                isLoading={loading}
                size={BUTTON_SIZE.MD}
                type={BUTTON_TYPE.SOLID}
                customBgColor={COLORS.PURPLE500}
              >
                <DSButton.Label>{getButtonName()}</DSButton.Label>
              </DSButton>
            )}
          />
        </div>
      )}
    </div>
  )
}

export const AITutorCourseCard = ({ onClick = null, course = null }) => {
  const [loading, setLoading] = useState(false)
  const useObject = course?.id
  const { data } = useCourseDetail(course, !useObject)
  const { t, locale } = useTranslation()
  const { data: aiTutorMembershipData } = useAITutorSummaryMembership('curriculum')
  const courseData = useObject ? { course } : data

  return (
    <div
      className="rounded-xl border-solid border-gray-100 border-[1.5px] p-[16px] flex flex-col gap-[24px] items-center justify-start self-stretch shrink-0 relative"
      style={{ boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.15)' }}
    >
      <div className="absolute right-[16px] top-[16px]">
        {courseData?.course?.progress === 100 ? (
          <QIcon_check_filled width={28} height={28} color={COLORS.PURPLE500} />
        ) : (
          <QIcon_check_outline width={28} height={28} color={COLORS.GRAY100} />
        )}
      </div>
      <div className="flex flex-col gap-[8px] items-center justify-start self-stretch shrink-0 relative">
        <div className="flex flex-row gap-[4px] items-start justify-start self-stretch shrink-0 relative">
          {courseData?.course?.is_current_course && (
            <div className="flex flex-row items-start justify-start shrink-0 relative">
              <div className="text-white bg-purple-500 rounded px-[6px] py-[2px] flex flex-row items-start justify-start shrink-0 relative">
                <div className="text-static-white text-left Caption-12Bd relative">
                  {t(n_ai_tutor.study.ongoing_label)}
                </div>
              </div>
            </div>
          )}
          <div className="flex flex-row items-start justify-start shrink-0 relative">
            <div className="bg-gray-100 rounded px-[6px] py-[2px] flex flex-row items-start justify-start shrink-0 relative">
              <div className="text-gray-600 text-left Caption-12Bd relative">{courseData?.course?.job_category}</div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-[8px] items-center justify-start self-stretch shrink-0 relative">
          <div className="text-left Title1-20Bd relative self-stretch overflow-hidden truncate truncate-2-lines">
            {getAITutorTranslation(courseData?.course?.title, locale)}
          </div>
          <div className="text-gray-600 text-left Body1-16Md relative self-stretch h-[22px] overflow-hidden truncate">
            {getAITutorTranslation(courseData?.course?.description, locale)}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2 items-center justify-start self-stretch shrink-0 relative">
        <img
          src={courseData?.course?.image_url || tempImageUrl}
          className="w-full rounded-[8px] mx-auto"
          style={{ aspectRatio: '288 / 160' }}
        />
      </div>
      <div className="flex flex-col gap-[4px] items-start justify-start self-stretch shrink-0 relative">
        <div className="flex flex-row items-center justify-between self-stretch shrink-0 relative">
          <div className="text-gray-500 text-left Caption-12Md relative">
            {courseData?.course?.done_lessons || 0} / {courseData?.course?.total_lessons || 0}{' '}
            {t(n_home.aispa.label_study)}
          </div>
        </div>
        <LinearProgress progress={courseData?.course?.progress} height={8} />
      </div>

      <DSButton
        isLoading={loading}
        onClick={() => onClick(course)}
        customBgColor={COLORS.PURPLE500}
        disabled={!courseData?.course?.is_current_course && aiTutorMembershipData?.is_trial}
      >
        <DSButton.Label>
          {courseData?.course?.is_current_course ? t(n_ai_tutor.study.continue_btn) : t(n_ai_tutor.study.start_btn)}
        </DSButton.Label>
      </DSButton>
    </div>
  )
}

export const AITutorKeyExpressionCard = ({ expression = null, onClick = null, key = null }) => {
  const sound = useContext(SoundContext)
  const [playing, setPlaying] = useState(false)

  const playAudio = () => {
    if (expression.audio_url) {
      sound.play([expression.audio_url]).then(() => setPlaying(false))
      setPlaying(true)
    }
  }

  const stopAudio = () => {
    sound.stop()
    setPlaying(false)
  }

  return (
    <div
      key={key}
      className="bg-white rounded-xl border border-gray-200 p-[16px] flex flex-col gap-[12px] items-start justify-start w-full shadow-[0px_2px_5px_2px_rgba(0,0,0,0.03)]"
    >
      <div className="flex flex-row items-start justify-between self-stretch">
        <div className="flex flex-col gap-[4px] items-start justify-start">
          <div className="text-black text-left text-[16px] leading-[24px] font-bold self-stretch">
            {expression.expression}
          </div>
          <div className="text-gray-500 text-left text-[14px] leading-[20px] self-stretch">{expression.meaning}</div>
        </div>
      </div>
      {expression.audio_url && (
        <>
          <div className="bg-gray-200 self-stretch h-[1px]"></div>
          <div className="flex flex-row items-center justify-between self-stretch">
            {playing ? (
              <QIcon_pause_circle width={20} height={20} onClick={stopAudio} />
            ) : (
              <QIcon_volumne width={20} height={20} onClick={playAudio} />
            )}
            {/* <div className="text-center text-[14px] leading-[20px] font-bold">발음 연습</div> */}
          </div>
        </>
      )}
    </div>
  )
}

export const AITutorKeyExpressionList = ({ lesson = null, expressions = [] }) => {
  const { locale } = useTranslation()
  const expressionsActivities = useMemo(
    () =>
      lesson?.lesson?.content?.activities
        ?.filter((e) => e.type === 'ExplanationActivity')
        ?.map((e) => ({
          expression: e.expression,
          meaning: locale === 'ko' ? e.kor : e.meaning,
          audio_url: e.audio_url, // TODO: set audio url
        })) || [],
    [lesson]
  )

  return [...(expressions || []), ...(expressionsActivities || [])].map((e, i) => (
    <SoundProvider key={`ai-tutor-key-expression-${i}`}>
      <AITutorKeyExpressionCard expression={e} />
    </SoundProvider>
  ))
}

export const AITutorStreakCard = ({ streak }) => {
  const { t } = useTranslation()

  const progress = useMemo(() => {
    if (streak < 7) return streak / 7 / 3
    else if (streak < 14) return (streak - 7) / 7 / 3 + 1 / 3
    else if (streak < 30) return (streak - 14) / (30 - 14) / 3 + 2 / 3
    else return 1
  }, [streak])

  const Achieve = ({ done }) => {
    return (
      <div
        className={`${
          done ? 'bg-yellow-500' : 'bg-gray-100'
        } rounded border-white border-[2px] flex flex-row items-center justify-center w-[24px] h-[24px]`}
      >
        <QIcon_certified width={20} height={20} color={done ? COLORS.WHITE : COLORS.GRAY400} />
      </div>
    )
  }

  return (
    <div className="rounded-[16px] border-solid border-gray-200 border-[1.5px] p-[16px] flex flex-col gap-[19px] items-start justify-start self-stretch">
      <div className="flex flex-row items-center justify-between self-stretch shrink-0">
        <div className="Title2-18Bd">{t(n_ai_tutor.acheivement.streak_challenge)}</div>
        <div className="Body1-16Md text-gray-600">{t(n_ai_tutor.acheivement.day(streak))}</div>
      </div>
      <div className="flex flex-col gap-[4px] items-start justify-start self-stretch">
        <div className="self-stretch shrink-0">
          <div className="bg-gray-100 rounded-[10px] w-full h-[12px]">
            <div
              className="bg-yellow-500 rounded-tl-[10px] rounded-bl-[10px] h-[12px]"
              style={{ width: `${progress * 100}%` }}
            ></div>
          </div>

          <div className="self-stretch shrink-0 top-[-18px] relative flex justify-between">
            <div></div>
            <Achieve done={streak >= 7} />
            <Achieve done={streak >= 14} />
            <Achieve done={streak >= 30} />
          </div>
        </div>
        <div className="self-stretch shrink-0 h-[19px] relative flex justify-between top-[-19px] mb-[-19px]">
          <div></div>
          <div className="text-gray-400 text-center Caption-12Md">{t(n_ai_tutor.acheivement.day(7))}</div>
          <div className="text-gray-400 text-center Caption-12Md">{t(n_ai_tutor.acheivement.day(14))}</div>
          <div className="text-gray-400 text-center Caption-12Md">{t(n_ai_tutor.acheivement.day(30))}</div>
        </div>
      </div>
    </div>
  )
}
